import { useCallback, useEffect, useRef, useState } from "react"
import { ChevronDown } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { ChatListSkeleton } from "./chat-list-skeleton"

const BATCH_SIZE = 20

export interface RecentChat {
  id: string
  title: string
  url: string
}

export interface SidebarRecentChatsProps {
  chats: RecentChat[]
  activeChatId?: string
  onChatClick?: (chatId: string) => void
  defaultOpen?: boolean
}

export function SidebarRecentChatsComponent({
  chats,
  activeChatId,
  onChatClick,
  defaultOpen = true
}: SidebarRecentChatsProps) {
  const [displayCount, setDisplayCount] = useState(BATCH_SIZE)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const isLoadingRef = useRef(false)
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined)

  const visibleChats = chats.slice(0, displayCount)
  const hasMore = displayCount < chats.length

  const hasMoreRef = useRef(hasMore)
  hasMoreRef.current = hasMore

  const chatsLengthRef = useRef(chats.length)
  chatsLengthRef.current = chats.length

  useEffect(() => {
    setDisplayCount(BATCH_SIZE)
  }, [chats.length])

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
    }
  }, [])

  const loadMore = useCallback(() => {
    if (isLoadingRef.current || !hasMoreRef.current) return
    isLoadingRef.current = true
    setIsLoadingMore(true)
    timeoutRef.current = setTimeout(() => {
      setDisplayCount((prev) => Math.min(prev + BATCH_SIZE, chatsLengthRef.current))
      isLoadingRef.current = false
      setIsLoadingMore(false)
    }, 400)
  }, [])

  useEffect(() => {
    const container = scrollContainerRef.current
    if (!container) return

    const isNearBottom = () => {
      const { scrollTop, clientHeight, scrollHeight } = container
      return scrollTop + clientHeight >= scrollHeight - 50
    }

    const handleScroll = () => {
      if (isNearBottom()) loadMore()
    }

    const handleWheel = (e: WheelEvent) => {
      if (e.deltaY > 0 && isNearBottom()) loadMore()
    }

    container.addEventListener("scroll", handleScroll, { passive: true })
    container.addEventListener("wheel", handleWheel, { passive: true })
    return () => {
      container.removeEventListener("scroll", handleScroll)
      container.removeEventListener("wheel", handleWheel)
    }
  }, [loadMore])

  return (
    <Collapsible defaultOpen={defaultOpen} className="hidden md:block">
      <div className="flex flex-col">
        <CollapsibleTrigger asChild>
          <button className="group/label flex cursor-pointer items-center justify-between px-3 py-[7px] text-xs font-semibold leading-[18px] tracking-[0.18px] text-text-subtlest hover:text-text-subtle">
            <span>Recent chats</span>
            <ChevronDown className="h-4 w-4 transition-transform duration-200 group-data-[state=open]/label:rotate-180" />
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div ref={scrollContainerRef} className="hark-scrollbar max-h-[calc(100vh-320px)]">
            <div className="flex flex-col">
              {chats.length === 0 ? (
                <div className="px-3 py-2 text-xs font-normal leading-[18px] tracking-[0.18px] text-text-subtlest">
                  No recent chats yet
                </div>
              ) : (
                visibleChats.map((chat) => {
                  const isActive = chat.id === activeChatId
                  return (
                    <Tooltip key={chat.id}>
                      <TooltipTrigger asChild>
                        <div className="flex h-8 items-center">
                          <div className="relative h-full w-5 shrink-0">
                            <div className="absolute left-2 top-0 h-full w-[1px] bg-border-default" />
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            data-active={isActive}
                            onClick={() => onChatClick?.(chat.id)}
                            className={`flex-1 h-full justify-start rounded-md px-2 text-xs font-normal leading-[18px] tracking-[0.18px] transition-colors duration-150 ease-in-out overflow-hidden text-ellipsis whitespace-nowrap ${
                              isActive
                                ? "text-text-default bg-bg-raised"
                                : "text-text-subtlest hover:text-text-default hover:bg-bg-raised"
                            }`}
                          >
                            {chat.title}
                          </Button>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent
                        side="bottom"
                        align="center"
                        className="bg-neutral-800 text-neutral-50 [&>span:first-child]:opacity-0"
                      >
                        {chat.title}
                      </TooltipContent>
                    </Tooltip>
                  )
                })
              )}
              {isLoadingMore && (
                <ChatListSkeleton count={Math.min(5, chats.length - displayCount)} />
              )}
            </div>
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  )
}
